import React, { useContext } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import { AccountIcon } from "@/components/icons/AccountIcon";
import { CloseMenuIcon } from "@/components/icons/CloseMenuIcon";
import { OpenMenuIcon } from "@/components/icons/OpenMenuIcon";
import { SearchIcon } from "@/components/icons/SearchIcon";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import SearchContext, { SearchContextType } from "@/context/SearchContext";
import UserContext, { UserContextType } from "@/context/UserContext";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";

import HeaderCartIcon from "./HeaderCartIcon";

interface HeaderIconsProps {
  navIsOpen?: boolean;
  showCart: boolean;
  showBurger?: boolean;
  theme?: ThemeTypes;
  isProductPage?: boolean;
  isAccountPage?: boolean;
  isQuizLp?: boolean;
  toggleMobileNav?: (open: boolean) => void;
  isSimple?: boolean;
  classNameBurger?: string;
  isIconVisibleOnRightSideOnly?: boolean;
  pathname?: string;
}

const HeaderIcons: React.FC<HeaderIconsProps> = ({
  navIsOpen,
  showCart,
  showBurger = true,
  theme = "master",
  isAccountPage,
  toggleMobileNav,
  isSimple = false,
  classNameBurger = "lg:hidden",
  isIconVisibleOnRightSideOnly = false,
  pathname,
}) => {
  const ctaText = "Take the Quiz";
  const dest = "/hair/brand/quiz/";

  const { toggleSearchDropdown, closeSearchDropdown } =
    useContext<SearchContextType>(SearchContext);

  const userState = useContext<UserContextType>(UserContext);

  const getAccountButton = (userState: UserContextType, className = "") => {
    return (
      <div className={cn(`ml-4 lg:ml-8`, isSimple ? "p-2.5" : "lg:pr-0", className)}>
        {userState.isLoggedIn ? (
          <LinkHandler
            path={`/account/`}
            onClick={() =>
              GA4.onNavigationClicked({
                destination: "/account/",
              })
            }
            ariaLabel="Account"
          >
            <AccountIcon className="w-6 h-6 text-nova-base-black" />
          </LinkHandler>
        ) : (
          <LinkHandler
            path={`/customer/account/login/`}
            onClick={() =>
              GA4.onNavigationClicked({
                destination: "/customer/account/login/",
              })
            }
            ariaLabel="Account"
          >
            <AccountIcon className="w-6 h-6 text-nova-base-black" />
          </LinkHandler>
        )}
      </div>
    );
  };

  return (
    <>
      {isSimple || isAccountPage ? null : (
        <div>
          <ButtonNova
            label={ctaText}
            ctaLink={dest}
            className="hidden lg:block desktop-button header-quiz-link [&.Primary.Default.Noicon]:py-3"
            trackObject={{
              destination: dest,
              module_location: "navigation",
              text: ctaText,
              type: "button",
            }}
          />
        </div>
      )}
      {isIconVisibleOnRightSideOnly && !isSimple && theme !== "men" ? (
        <div className="search-icon-container ml-0 md:ml-[15px] lg:ml-[30px]">
          <button
            onClick={() => {
              if (navIsOpen) {
                toggleMobileNav(false);
              }
              toggleSearchDropdown();
            }}
            aria-label={"search"}
            className="flex items-center"
          >
            <SearchIcon className="text-nova-base-black" />
          </button>
        </div>
      ) : null}
      {(isSimple && isIconVisibleOnRightSideOnly) || isAccountPage
        ? null
        : getAccountButton(userState, "hidden lg:block")}
      {isSimple && !showCart && !isAccountPage ? (
        <div className={"lg:hidden"}>{getAccountButton(userState)}</div>
      ) : null}
      {showCart ? (
        <div className={`ml-4 md:ml-6 lg:ml-8 ${isSimple ? "mr-0" : "mr-2.5 lg:mr-0"}`}>
          <HeaderCartIcon pathname={pathname} />
        </div>
      ) : null}
      {!isSimple && !isAccountPage && showBurger ? (
        <div className={`w-10   ${classNameBurger}`}>
          <div className="burger-container lg:hidden md:pl-[15px]">
            <button
              id={"HamburgerOpenCloseIcon"}
              className={cn("h-[52px] py-[13px]", navIsOpen ? "px-[13px]" : "px-[11px]")}
              onClick={() => {
                closeSearchDropdown(false);
                toggleMobileNav(!navIsOpen);
              }}
              aria-label="Toggle Navigation"
              tabIndex={6}
            >
              <CloseMenuIcon className="close-nav text-nova-base-black" />
              <OpenMenuIcon className="open-nav text-nova-base-black" />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default HeaderIcons;
